import { Component, OnInit } from '@angular/core';
import { HeaderService } from "@app/core/services/header.service";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { saveAs } from 'file-saver';
import Swal from "sweetalert2";
import { ImportService } from "./import.service";
import { Carrier } from "@app/core/enums/carrier";
import { GetBatchShipmentsHistoryResult } from "@app/shared/CSDAModels/GetBatchShipmentsHistoryResult";
import { ImportOrdersHeader } from "@app/shared/CSDAModels/ImportOrdersHeader";
import moment from 'moment';
import { MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatDialog } from '@angular/material/dialog';
import { BatchShipmentsReportComponent } from "@app/shared/components/batch-shipments-report/batch-shipments-report.component";
import { GetBatchShipmentsReportResult } from "@app/shared/CSDAModels/GetBatchShipmentsReportResult";
import { MatSelect } from "@angular/material/select";
import { CarrierOption } from "@app/shared/CSDAModels/CarrierOption";
import { ImportResult } from "@app/shared/CSDAModels/ImportResult";
import { UsersRights } from "@app/shared/CSDAModels/UserRights";


export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'YYYY/MM/DD',
  },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  },
};

@Component({
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss'],
  standalone: false,
  providers: [
    // 👇 The critical part: use MomentDateAdapter instead of default native
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],  
})

export class ImportComponent implements OnInit {

  isProcessing: boolean = false;

  // top form state
  isBatchEnabled: boolean = false;
  batchDescription: string = '';
  batchEmail: string = '';
  batchCarrier: string = '';
  batchServiceType: string = '';

  carriers: string[] = [
    'ALL',
    'DHL',
    'FEDEX',
    'GLOBEX',
    'LOOMIS',
    'PUROLATOR'
  ];

  CurrentUsersRights: UsersRights[];
  batchShipmentsAllowed: boolean = false;
  selectedFile: File | null = null;

  // pickup side panel state
  isPickupEnabled: boolean = false;
  pickupDate: moment.Moment | null = moment();
  pickupReadyTime: string = '09:00';
  pickupCloseTime: string = '17:00';
  pickupCustomerRef: string = '';
  pickupInstructions: string = '';

  // current user email (used for hint/default)
  currentUserEmail: string = ''; // you can preload this

  // history table data
  historyRows: ImportOrdersHeader[] = [];
  serviceTypes: CarrierOption[] = [];
  filteredServiceTypes: CarrierOption[] = [];

  displayedColumns: string[] = [
    'IMH_ImportedDate',
    'IMH_Description',
    'IMH_Carrier',
    'IMH_ServiceType',
    'IMH_Email',
    'IMH_OrderCount',
    'IMH_ImportSuccessCount',
    'IMH_ImportFailureCount',
    'actions'
  ];

  constructor(
    private translateService: TranslateService,
    private headerService: HeaderService,
    private importService: ImportService,
    private dateAdapter: DateAdapter<any>,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    //-------------------
    // Set the page title
    //-------------------
    this.headerService.setTitle(this.translateService.instant("app.importOrders.title"));

    //---------------------------------------------------------------------------------
    // Subscribe to the translate service to update the title when the language changes
    //---------------------------------------------------------------------------------
    this.translateService.onLangChange.subscribe((params: LangChangeEvent) => {
      this.headerService.setTitle(this.translateService.instant("app.importOrders.title"));
    });

    //------------------------------------------------------------------------------------
    // Set default email (if you eventually inject auth/user service you can replace this)
    //------------------------------------------------------------------------------------
    this.batchEmail = sessionStorage.getItem("CurrentUserEmail");

    //--------------------------------------------------------------------------
    // Set the boolean to know if the user is allowed to use the Batch Shipments
    //--------------------------------------------------------------------------
		this.CurrentUsersRights = JSON.parse(sessionStorage.getItem("CurrentUserRights"));

		if(this.CurrentUsersRights.find(c => c.Uri_Rit_Ref == 2 || c.Uri_Rit_Ref == 7) != null)
			this.batchShipmentsAllowed = true;
		
    //----------------------------------------
    // Initial load of Batch Shipments History
    //----------------------------------------
    if(this.batchShipmentsAllowed)
      this.refresh();
  }
  
  //--------------------------------------------------------------
  // Filtering the list of services based on the carrier selection
  //--------------------------------------------------------------
  onCarrierChanged(): void {
    if (this.batchCarrier) {
      const carrierName = this.batchCarrier.toLowerCase();

      this.filteredServiceTypes = this.serviceTypes.filter(
        s => s.Carrier?.toLowerCase() === carrierName
      );
    } else {
      this.filteredServiceTypes = [];
    }

    this.batchServiceType = '';
  }

  toggleBatch(event: MatCheckboxChange) {
    this.isBatchEnabled = event.checked;
    //this.isPickupEnabled = this.isBatchEnabled
  }

  // -------------------------
  // Template download
  // -------------------------
  getTemplate() {
    this.isProcessing = true;
    this.importService.getTemplate().subscribe(
      (data: Blob) => {
        this.isProcessing = false;
        saveAs(data, "Orders.xlsx");
      },
      err => {
        this.isProcessing = false;
        Swal.fire("", err.message, "error");
      }
    );
  }

  // -------------------------
  // History refresh
  // -------------------------
  refresh() {
    this.isProcessing = true;

    this.importService.getBatchShipmentsHistory().subscribe(
      (result: GetBatchShipmentsHistoryResult) => {
        this.isProcessing = false;
        this.historyRows = result?.ImportOrdersHeaders ?? [];
        this.serviceTypes = result?.ServiceTypes ?? [];
      },
      (err: { message: string; }) => {
        this.isProcessing = false;
        Swal.fire("", err.message, "error");
      }
    );
  }

  // -------------------------
  // Row actions
  // -------------------------
  waybills(row: ImportOrdersHeader) {
    this.isProcessing = true;
    this.importService.getBatchWaybills(row.IMH_Id).subscribe({
      next: (blob: Blob | null) => {
        if (!blob || blob.size == 0) {
          this.isProcessing = false;
          Swal.fire('', this.translateService.instant("app.batchShipments.noPDF"), 'warning');
          return;
        }

        const pdfUrl = URL.createObjectURL(blob);
        this.isProcessing = false;
        window.open(pdfUrl, '_blank');
      },
      error: (err: any) => {
        this.isProcessing = false;
        console.error('Error fetching waybills PDF', err);
        Swal.fire('', 'Error generating waybills PDF', 'error');
      }
    });
  }

  report(row: ImportOrdersHeader) {
    this.isProcessing = true;

    this.importService.getBatchShipmentsReport(row.IMH_Id).subscribe({
      next: (result: GetBatchShipmentsReportResult | null) => {
        this.isProcessing = false;

        if (!result || !result.BatchShipmentsOrders?.length) {
          Swal.fire('', this.translateService.instant("app.batchShipments.exceptionMessage"), 'info');
          return;
        }

        this.dialog.open(BatchShipmentsReportComponent, {
          width: '1100px',
          maxHeight: '90vh',
          panelClass: 'batch-report-dialog',
          data: { shipments: result.BatchShipmentsOrders }
        });
      },
      error: (err: { message: string }) => {
        this.isProcessing = false;
        Swal.fire('', err.message, 'error');
      },
      complete: () => {
        // optional: cleanup or logging
      }
    });
  }

  reprocess(row: ImportOrdersHeader) {
    this.isProcessing = true;

    this.importService.reprocessShipments(row.IMH_Id).subscribe({
      next: (result: ImportResult | null) => {
        this.isProcessing = false;
      },
      error: (err: { message: string }) => {
        this.isProcessing = false;
        Swal.fire('', err.message, 'error');
      },
      complete: () => {
        
      }
    });
  }
}
